import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItemButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

/** Left rail: list of menus with create / select / delete. */
export default function MenuListPanel({
  menus,
  selectedId,
  onSelect,
  onCreate,
  onDelete,
  /* Change #100366 - optional per-row copy, so a menu can be duplicated
     without opening it first. Omitted (Super Admin) = no button. */
  onDuplicate,
  /* Task 24886 - a company's list now mixes menus CRMit rolled out with menus
     the company built, and they behave differently, so the list says which is
     which BEFORE you click one and find the builder inert. */
  scopePills = false,
  /* Per-menu caption, e.g. "Rolled out to 12 of 14 companies" in Super Admin. */
  getSubtitle,
  title = "Menus",
  createPlaceholder = "New menu name",
}) {
  const [name, setName] = useState("");

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    setName("");
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>

      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          placeholder={createPlaceholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        <Button
          variant="contained"
          color="success"
          onClick={submit}
          disabled={!name.trim()}
          startIcon={<AddIcon />}
        >
          Add
        </Button>
      </Stack>

      <List sx={{ mt: 1.5, maxHeight: 480, overflowY: "auto" }}>
        {(menus || []).map((menu) => {
          const isSelected = String(menu.Id) === String(selectedId);
          // A menu is global when it carries the link back to the master
          // definition. FlagMasterLockNoChanges is the older, weaker signal -
          // honoured too, so a menu locked before this task still reads right.
          const isGlobal = Boolean(
            menu.MasterMenuNav_Id || Number(menu.FlagMasterLockNoChanges) === 1
          );
          const subtitle = getSubtitle ? getSubtitle(menu) : null;
          return (
            <ListItemButton
              key={menu.Id}
              selected={isSelected}
              onClick={() => onSelect(menu.Id)}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                border: "1px solid",
                borderColor: isSelected ? "primary.main" : "divider",
              }}
            >
              <MenuOpenIcon
                fontSize="small"
                sx={{ mr: 1, color: isSelected ? "primary.main" : "text.disabled" }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {menu.Name}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ mt: 0.25 }}
                >
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    #{menu.Id}
                  </Typography>
                  {scopePills && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={isGlobal ? "info" : "default"}
                      label={isGlobal ? "GLOBAL" : "COMPANY SPECIFIC"}
                      sx={{ height: 18, fontSize: 10, letterSpacing: 0.4 }}
                    />
                  )}
                </Stack>
                {subtitle ? (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.disabled", display: "block" }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
              {onDuplicate && (
                <Tooltip
                  title={
                    scopePills && isGlobal
                      ? "Clone to company copy"
                      : "Duplicate menu"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate(menu);
                    }}
                    sx={{ color: "primary.main", mr: 0.5 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Deleting a rolled-out menu is refused by the API, so the
                  button is not offered - a button whose only outcome is an
                  error message is worse than no button. */}
              {scopePills && isGlobal ? (
                <Tooltip title="Managed by CRMit - clone it to make a copy you can change">
                  <LockOutlinedIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Delete menu">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(menu.Id);
                    }}
                    sx={{ color: "error.light" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ListItemButton>
          );
        })}
        {(!menus || menus.length === 0) && (
          <Typography variant="body2" sx={{ p: 2, color: "text.disabled" }}>
            No menus yet. Create one above to get started.
          </Typography>
        )}
      </List>
    </Box>
  );
}
